import React, { useEffect } from "react";
import { HashRouter, Redirect, Route, Switch } from "react-router-dom";

import RouteGuard from "@components/Routes/RouteGuard";
import { TypesPermissions } from "@components/Routes/TypesPermissions";
import { LegacyTypesPermissions } from "@components/Routes/legacyTypesPermissions";
import {
  getAdaptivePermissionMatcher as buildAdaptivePermissionMatcher,
  getRouteGuardRules as buildRouteGuardRules,
} from "@utils/permissions/getAdaptivePermissionMatcher";
import { getFirstPermittedRoute } from "@utils/permissions/getFirstPermittedRoute";
import CommunicatedPage from "@pages/Admin/Communications/";
import DriverAndCustomer from "@pages/Admin/DriverAndCustomer";
import ListInvoicesImages from "@pages/Admin/ListInvoicesImages";
import NozzleAdmin from "@pages/Admin/Nozzle";
import ProductBlock from "@pages/Admin/ProductBlock";
import ReclamationConsultAdminMode from "@pages/Admin/ReclamationConsultAdminMode";
import ReclamationDetailsAdminMode from "@pages/Admin/ReclamationsDetailsAdminMode";
import { AppointmentPrice } from "@pages/User/Appointment/AppointmentPrice";
import DashPoshPage from "@pages/User/DashPosh";
import InstantRewardPage from "@pages/User/DashPosh/InstantRewardPage";
import Employee from "@pages/User/Employee";
import GoalsAndCommissions from "@pages/User/GolsAndComissions";
import NewRequest from "@pages/User/NewRequest/";
import NewRequestV2 from "@pages/User/NewRequestV2/";
import NewRequestOld from "@pages/User/NewRequestOld";
import NotFoundPage from "@pages/User/NotFoundPage";
import Nozzle from "@pages/User/Nozzle";
import { NewOrderPage } from "@pages/User/OrderSimulation";
import PerformanceManagement from "@pages/User/PerformanceManagement";
import ComissionCalculator from "@pages/User/PerformanceManagement/Pages/CommissionCalculator";
import PeakTimeHistory from "@pages/User/PerformanceManagement/Pages/PeakTimeHistory";
import Reclamation from "@pages/User/Reclamation";
import ReclamationConsult from "@pages/User/ReclamationConsult";
import ReclamationDetails from "@pages/User/ReclamationDetails";
import RoadLoad from "@pages/User/RoadLoad";
import YourSales from "@pages/User/YourSales";
import { generatePermissionMatcher } from "@utils/index";
import { HandoutProvider } from "../../context/HandoutContext";
import AddUserPage from "../../pages/Admin/UserManagement/AddUser";
import EditUserPage from "../../pages/Admin/UserManagement/EditUser";
import HandoutsAdminPage from "../../pages/Admin/Handouts";
import HomePageAdmin from "../../pages/Admin/Home";
import ListUsersPage from "../../pages/Admin/UserManagement/ListUsers";
import AppointmentInvoices from "../../pages/User/Appointment/AppointmentInvoices";
import AppointmentTitle from "../../pages/User/Appointment/AppointmentTitle";
import HandoutsUserPage from "../../pages/User/Handouts";
import HomePage from "../../pages/User/Home";
import MyOrders from "../../pages/User/MyOrders";
import OrderPlaced from "../../pages/User/OrderPlaced";
import ROUTES from "../../config/routes";
import ProfileRoute from "./ProfileRoute";
import Discount from "@pages/User/Discount";
import Product from "@pages/User/Product";
import Programming from "@pages/User/RoadLoad/sections/Programming";
import MyDrivers from "@pages/User/MyDrivers";
import NotificationsPage from "@pages/Admin/Notifications";

import { NotificationProvider } from "@pages/Admin/Notifications/context";
import NotificationPortalProvider from "@context/notification/provider";
import { useAuth } from "@context/auth/AuthContext";
import { NOTIFICATION_FLAG } from "@utils/featureFlags";
import { useSystemModulesAuthFlag } from "@hooks/useSystemModulesAuthFlag";

const Routes = () => {
  const { permissionSet } = useAuth();
  const useSystemModulesAuth = useSystemModulesAuthFlag();

  const adaptivePermissionMatcher = (
    v2Permission: string,
    legacyPermission?: string
  ) =>
    buildAdaptivePermissionMatcher(
      v2Permission,
      legacyPermission,
      useSystemModulesAuth
    );

  const routeGuardRules = (v2Permission: string, legacyPermission?: string) =>
    buildRouteGuardRules(v2Permission, legacyPermission, useSystemModulesAuth);

  const permissionSystemModules = permissionSet?.SYSTEM_MODULES;
  const firstPermittedRoute = getFirstPermittedRoute(
    permissionSystemModules,
    permissionSet.ROLES,
    useSystemModulesAuth
  );

  useEffect(() => {
    const firstAccessDetected = JSON.parse(
      sessionStorage.getItem("detectedFirstAccess") ?? ""
    );

    if (firstAccessDetected) return;

    if (permissionSystemModules?.length > 0 && firstPermittedRoute) {
      window.location.href = `#${firstPermittedRoute}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HashRouter>
      <HandoutProvider>
        <NotificationPortalProvider>
          <Switch>
            <ProfileRoute
              path={ROUTES.USER_ROUTES.HOME}
              exact
              component={() => (
                <RouteGuard
                  flag="user_sidebar_home_user"
                  rules={generatePermissionMatcher(TypesPermissions.HOME)}
                >
                  <HomePage />
                </RouteGuard>
              )}
            />

            {/* Funcionarios */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.EMPLOYEE}
              component={() => (
                <RouteGuard
                  flag="user_sidebar_employee"
                  rules={adaptivePermissionMatcher(
                    TypesPermissions.EMPLOYEE,
                    LegacyTypesPermissions.EMPLOYEE
                  )}
                >
                  <Employee />
                </RouteGuard>
              )}
            />

            {/* Metas e comissoes */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.GOALS_AND_COMMISSIONS}
              component={() => (
                <RouteGuard
                  flag="user_sidebar_goal_and_commissions"
                  rules={generatePermissionMatcher(
                    TypesPermissions.GOALS_AND_COMMISSIONS
                  )}
                >
                  <GoalsAndCommissions />
                </RouteGuard>
              )}
            />

            {/* Bico e Produto */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.NOZZLE}
              component={() => (
                <RouteGuard
                  flag="user_sidebar_product_nozzle"
                  rules={generatePermissionMatcher(TypesPermissions.NOZZLES)}
                >
                  <Nozzle />
                </RouteGuard>
              )}
            />

            {/* Suas Vendas */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.SALES}
              component={() => (
                <RouteGuard
                  flag="user_sidebar_your_sales"
                  rules={adaptivePermissionMatcher(
                    TypesPermissions.COLETA,
                    LegacyTypesPermissions.COLETA
                  )}
                >
                  <YourSales />
                </RouteGuard>
              )}
            />

            {/* Gestao de desempenho */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.PERFORMANCE_MANAGEMENT}
              component={() => (
                <RouteGuard
                  flag="user_sidebar_hide_performance_management"
                  rules={routeGuardRules(
                    TypesPermissions.PERFORMANCE_MANAGEMENT,
                    undefined
                  )}
                >
                  <PerformanceManagement />
                </RouteGuard>
              )}
            />

            {/* Meus motoristas */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.MY_DRIVERS}
              component={() => {
                return (
                  <RouteGuard
                    flag="user_sidebar_my_drivers"
                    rules={adaptivePermissionMatcher(
                      TypesPermissions.DRIVER,
                      LegacyTypesPermissions.PORTAL_CLIENTE
                    )}
                  >
                    <MyDrivers />
                  </RouteGuard>
                );
              }}
            />

            {/* Carregamento rodoviário (Agenda e Vem) */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.ROADLOAD}
              component={() => {
                return (
                  <RouteGuard
                    flag="user_sidebar_road_load"
                    rules={adaptivePermissionMatcher(
                      TypesPermissions.APPOINTMENT,
                      LegacyTypesPermissions.DRIVER
                    )}
                  >
                    <RoadLoad />
                  </RouteGuard>
                );
              }}
            />

            <ProfileRoute
              path={ROUTES.USER_ROUTES.ORDER_PLACED}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(TypesPermissions.ORDER, undefined)}
                >
                  <OrderPlaced />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.USER_ROUTES.NEW_ORDER_OLD}
              component={() => (
                <RouteGuard
                  rules={generatePermissionMatcher(TypesPermissions.ORDER)}
                >
                  <NewRequestOld />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.USER_ROUTES.NEW_ORDER}
              component={() => (
                <RouteGuard
                  rules={generatePermissionMatcher(TypesPermissions.ORDER)}
                >
                  <NewRequest />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.USER_ROUTES.NEW_ORDER_V2}
              component={() => (
                <RouteGuard
                  rules={generatePermissionMatcher(TypesPermissions.ORDER)}
                >
                  <NewRequestV2 />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.USER_ROUTES.MY_ORDERS}
              component={() => (
                <RouteGuard
                  rules={generatePermissionMatcher(TypesPermissions.ORDER)}
                >
                  <MyOrders />
                </RouteGuard>
              )}
            />

            {/* Consultar Titulos */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.APPOINTMENT_TITLE}
              component={() => (
                <RouteGuard
                  rules={adaptivePermissionMatcher(
                    TypesPermissions.TITLES,
                    LegacyTypesPermissions.APPOINTMENTS_TITLE
                  )}
                >
                  <AppointmentTitle />
                </RouteGuard>
              )}
            />

            {/* Consultar Notas Fiscais */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.APPOINTMENT_INVOICES}
              component={() => (
                <RouteGuard
                  rules={adaptivePermissionMatcher(
                    TypesPermissions.INVOICES,
                    LegacyTypesPermissions.APPOINTMENTS_INVOICES
                  )}
                >
                  <AppointmentInvoices />
                </RouteGuard>
              )}
            />

            {/* Metricas de campanha */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.DASH_POSH}
              component={() => (
                <RouteGuard
                  flag="user_sidebar_metrics_and_campaigns"
                  rules={generatePermissionMatcher(
                    TypesPermissions.CAMPAIGN_METRICS
                  )}
                >
                  <DashPoshPage />
                </RouteGuard>
              )}
            />

            {/* Comunicados */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.HANDOUTS}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.ANNOUNCEMENTS,
                    undefined
                  )}
                >
                  <HandoutsUserPage />
                </RouteGuard>
              )}
            />

            {/* Simular Carregamento */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.SIMULATE_ORDER}
              component={() => (
                <RouteGuard
                  flag="user_sidebar_hide_simulate_loading"
                  rules={adaptivePermissionMatcher(
                    TypesPermissions.SIMULATION,
                    LegacyTypesPermissions.SIMULATION
                  )}
                >
                  <NewOrderPage />
                </RouteGuard>
              )}
            />

            {/* Desconto */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.DISCOUNT}
              component={() => (
                <RouteGuard
                  flag="user_sidebar_hide_discount"
                  rules={adaptivePermissionMatcher(
                    TypesPermissions.DISCOUNT,
                    LegacyTypesPermissions.DISCOUNT
                  )}
                >
                  <Discount />
                </RouteGuard>
              )}
            />

            {/* Preco de Produto */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.APPOINTMENT_PRICE}
              component={() => (
                <RouteGuard
                  flag="user_sidebar_price_product_atem"
                  rules={adaptivePermissionMatcher(
                    TypesPermissions.PRODUCT_PRICES,
                    LegacyTypesPermissions.PRODUCT_PRICE
                  )}
                >
                  <AppointmentPrice />
                </RouteGuard>
              )}
            />

            {/* Produto */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.PRODUCT}
              component={() => (
                <RouteGuard
                  flag="user_sidebar_hide_discount"
                  rules={adaptivePermissionMatcher(
                    TypesPermissions.DISCOUNT,
                    LegacyTypesPermissions.DISCOUNT
                  )}
                >
                  <Product />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.USER_ROUTES.RECLAMATION}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(TypesPermissions.SAC, undefined)}
                >
                  <Reclamation />
                </RouteGuard>
              )}
            />
            <ProfileRoute
              path={ROUTES.USER_ROUTES.RECLAMATION_CONSULT}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(TypesPermissions.SAC, undefined)}
                >
                  <ReclamationConsult />
                </RouteGuard>
              )}
            />
            <ProfileRoute
              path={ROUTES.USER_ROUTES.RECLAMATION_DETAILS}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(TypesPermissions.SAC, undefined)}
                >
                  <ReclamationDetails />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={`${ROUTES.USER_ROUTES.INSTANT_REWARD}/:companiesId`}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.CAMPAIGN_METRICS,
                    undefined
                  )}
                >
                  <InstantRewardPage />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.USER_ROUTES.NEW_PROGRAMMING}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.APPOINTMENT,
                    undefined
                  )}
                >
                  <Programming />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={`${ROUTES.USER_ROUTES.EDIT_PROGRAMMING}/:programmingId`}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.APPOINTMENT,
                    undefined
                  )}
                >
                  <Programming />
                </RouteGuard>
              )}
            />

            {/* Sub-rotas de Gestão de Desempenho */}
            <ProfileRoute
              path={ROUTES.USER_ROUTES.COMMISSION_CALCULATOR}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.PERFORMANCE_MANAGEMENT,
                    undefined
                  )}
                >
                  <ComissionCalculator />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.USER_ROUTES.PEAK_TIME_HISTORY}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.PERFORMANCE_MANAGEMENT,
                    undefined
                  )}
                >
                  <PeakTimeHistory />
                </RouteGuard>
              )}
            />

            {/* ---------------- ADMIN ROUTES ---------------- */}

            <ProfileRoute
              exact
              path={ROUTES.ADMIN_ROUTES.HOME}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.ADMIN_HOME,
                    undefined
                  )}
                >
                  <HomePageAdmin />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.ADMIN_ROUTES.NEW_USER}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.ADMIN_USERS,
                    undefined
                  )}
                >
                  <AddUserPage />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.ADMIN_ROUTES.LIST_USERS}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.ADMIN_USERS,
                    undefined
                  )}
                >
                  <ListUsersPage />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.ADMIN_ROUTES.EDIT_USER}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.ADMIN_USERS,
                    undefined
                  )}
                >
                  <EditUserPage />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.ADMIN_ROUTES.ALL_COMMUNICATION}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.ADMIN_ANNOUNCEMENTS,
                    undefined
                  )}
                >
                  <CommunicatedPage />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.ADMIN_ROUTES.HANDOUTS}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.ADMIN_ANNOUNCEMENTS,
                    undefined
                  )}
                >
                  <HandoutsAdminPage />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.ADMIN_ROUTES.LIST_INVOICES_IMAGES}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.ADMIN_INVOICES,
                    undefined
                  )}
                >
                  <ListInvoicesImages />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.ADMIN_ROUTES.PRODUCT_BLOCK}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.ADMIN_PRODUCT_BLOCK,
                    undefined
                  )}
                >
                  <ProductBlock />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.ADMIN_ROUTES.RECLAMATION_ANALYSIS}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(TypesPermissions.ADMIN_SAC, undefined)}
                >
                  <ReclamationConsultAdminMode />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.ADMIN_ROUTES.RECLAMATION_DETAILS_ADM}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(TypesPermissions.ADMIN_SAC, undefined)}
                >
                  <ReclamationDetailsAdminMode />
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.ADMIN_ROUTES.NOZZLE}
              component={() => (
                <RouteGuard
                  rules={routeGuardRules(
                    TypesPermissions.ADMIN_NOZZLES,
                    undefined
                  )}
                >
                  <NozzleAdmin />
                </RouteGuard>
              )}
            />

            {/* Motorista vs Cliente — TODO: mapeamento aproximado; página usa serviço próprio de driver-customer */}
            <ProfileRoute
              path={ROUTES.ADMIN_ROUTES.DRIVER_AND_CUSTOMER}
              exact
              component={() => (
                <RouteGuard
                  flag="admin_sidebar_driver_and_customer"
                  rules={routeGuardRules(
                    TypesPermissions.ADMIN_USERS,
                    undefined
                  )}
                >
                  <DriverAndCustomer />
                </RouteGuard>
              )}
            />

            {/* Modulo de Notificacoes com FF */}
            <ProfileRoute
              path={`${ROUTES.ADMIN_ROUTES.NOTIFICATIONS.ROOT}`}
              component={() => (
                <RouteGuard
                  flag={NOTIFICATION_FLAG}
                  rules={routeGuardRules(
                    TypesPermissions.ADMIN_NOTIFICATIONS,
                    undefined
                  )}
                >
                  <NotificationProvider>
                    <Switch>
                      {/* Listar todas */}
                      <Route
                        exact
                        path={ROUTES.ADMIN_ROUTES.NOTIFICATIONS.ROOT}
                        component={NotificationsPage.ListNotification}
                      />

                      {/* Criar */}
                      <Route
                        exact
                        path={`${ROUTES.ADMIN_ROUTES.NOTIFICATIONS.CREATE}`}
                        component={NotificationsPage.CreateNotification}
                      />

                      {/* Editar */}
                      <Route
                        exact
                        path={`${ROUTES.ADMIN_ROUTES.NOTIFICATIONS.EDIT}/:id`}
                        component={NotificationsPage.EditNotification}
                      />

                      {/* Visualizar */}
                      <Route
                        exact
                        path={`${ROUTES.ADMIN_ROUTES.NOTIFICATIONS.VIEW}/:id`}
                        component={NotificationsPage.ViewNotification}
                      />
                    </Switch>
                  </NotificationProvider>
                </RouteGuard>
              )}
            />

            <ProfileRoute
              path={ROUTES.USER_ROUTES.NOTFOUND}
              component={() => <NotFoundPage />}
            />

            <Redirect path="*" to={firstPermittedRoute} />
          </Switch>
        </NotificationPortalProvider>
      </HandoutProvider>
    </HashRouter>
  );
};

export default Routes;
